import { useEffect, useRef } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Fire a cancel call for a PENDING_GUEST draft the moment the user leaves
 * the screen without approving — regardless of HOW they leave (route
 * navigation, bottom nav, back button, tab close, app background).
 *
 * This closes the "stale draft" root cause: without this hook, closing
 * the app on the awaiting screen leaves a PENDING_GUEST row behind that
 * can resurface for the guest or another demo user later. The backend
 * 60s TTL is a safety net; this hook kills the draft the moment the
 * user bails.
 *
 * Usage:
 *   useAutoCancelDraft({ txnId, status, kind: 'real' })  // real txns
 *   useAutoCancelDraft({ txnId, status, kind: 'demo' })  // demo txns
 *
 * Pass the LIVE status so we don't cancel after a PAID/CANCELLED/EXPIRED
 * transition.
 */
export function useAutoCancelDraft({ txnId, status, kind }) {
  const statusRef = useRef(status);
  statusRef.current = status;

  useEffect(() => {
    if (!txnId) return undefined;

    const buildRequest = () => {
      const token = localStorage.getItem('token');
      if (!token) return null;
      const url =
        kind === 'demo'
          ? `${API}/demo-transactions/${txnId}/cancel`
          : `${API}/transactions/${txnId}/cancel`;
      const method = kind === 'demo' ? 'POST' : 'PUT';
      return { url, method, token };
    };

    // Fires a cancel call that will complete even if the tab is closing.
    // `fetch(..., { keepalive: true })` keeps the request alive after the
    // document is gone — unlike sendBeacon it also lets us set an auth
    // header. Fire-and-forget: failure here falls back to the 60s TTL.
    const fireCancel = () => {
      if (statusRef.current !== 'PENDING_GUEST') return;
      const req = buildRequest();
      if (!req) return;
      try {
        fetch(req.url, {
          method: req.method,
          keepalive: true,
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${req.token}`,
          },
          body: '{}',
        }).catch(() => {});
      } catch (_) {
        /* best-effort */
      }
    };

    const onPageHide = () => fireCancel();
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') fireCancel();
    };

    window.addEventListener('pagehide', onPageHide);
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      window.removeEventListener('pagehide', onPageHide);
      document.removeEventListener('visibilitychange', onVisibility);
      // Cleanup on unmount — covers in-app route changes (bottom nav,
      // back button inside the SPA).
      fireCancel();
    };
  }, [txnId, kind]);
}
